import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type NotificationQuery } from '@/api/notifications.api';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (query: NotificationQuery) => [...notificationKeys.all, 'list', query] as const,
  unread: ['notifications', 'unread-count'] as const,
};

const refreshNotifications = (client: ReturnType<typeof useQueryClient>) => client.invalidateQueries({ queryKey: notificationKeys.all });

export const useNotifications = (query: NotificationQuery = {}, enabled = true) => useQuery({
  queryKey: notificationKeys.list(query),
  queryFn: () => notificationsApi.list(query),
  enabled,
});

export const useNotificationUnreadCount = (enabled = true) => useQuery({
  queryKey: notificationKeys.unread,
  queryFn: notificationsApi.unreadCount,
  enabled,
  refetchInterval: 30_000,
});

export function useMarkNotificationRead() {
  const client = useQueryClient();
  return useMutation({ mutationFn: notificationsApi.markRead, onSuccess: () => refreshNotifications(client) });
}

export function useMarkAllNotificationsRead() {
  const client = useQueryClient();
  return useMutation({ mutationFn: notificationsApi.markAllRead, onSuccess: () => refreshNotifications(client) });
}

export function useMarkRelatedNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ relatedEntityType, relatedEntityId }: { relatedEntityType: string; relatedEntityId: string }) => notificationsApi.markRelatedRead(relatedEntityType, relatedEntityId),
    onSuccess: () => refreshNotifications(client),
  });
}
